import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import styled from 'styled-components';
import { authService } from '../services/auth';
import MeditationDisplay from '../components/meditation/MeditationDisplay';
import { config } from '../config';
import SEO from '../components/common/SEO';
import ShareModal from '../components/common/ShareModal';
import SignInModal from '../components/auth/SignInModal';
import { FreeListenBanner } from '../components/common/FreeListenBanner';
import { 
  FaThumbsUp, 
  FaThumbsDown, 
  FaStar, 
  FaShare, 
  FaUserPlus,
  FaChevronDown,
  FaChevronUp,
  FaMusic,
  FaBrain,
  FaFileAudio,
  FaVolumeUp
} from 'react-icons/fa';
import { Icon } from '../components/common/Icon';
import Comments from '../components/comments/Comments';

/**
 * Container for the meditation page content
 */
const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

/**
 * Loading state styling
 */
const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: white;
  font-size: 1.2rem;
`;

/**
 * Error state styling
 */
const Error = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #ff6b6b;
  font-size: 1.2rem;
  text-align: center;
`;

/**
 * Not found state styling
 */
const NotFound = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: white;
  text-align: center;
`;

const NotFoundTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const NotFoundMessage = styled.p`
  font-size: 1.1rem;
  opacity: 0.8;
`;

const ContentBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  margin-top: 24px;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 0px ;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const Username = styled.h3`
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const UserLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AvatarLink = styled(Link)`
  display: inline-block;
 transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PublishedDate = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;
const ViewCount = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ 
  $active?: boolean; 
  $variant?: 'primary' | 'secondary' | 'follow' | 'thumbsUp' | 'thumbsDown' | 'favorite' 
}>`
  background: ${props => {
    if (props.$variant === 'follow' && props.$active) return 'rgba(76, 175, 80, 0.2)';
    if (props.$variant === 'thumbsUp' && props.$active) return 'rgba(76, 175, 80, 0.2)';
    if (props.$variant === 'thumbsDown' && props.$active) return 'rgba(244, 67, 54, 0.2)';
    if (props.$variant === 'favorite' && props.$active) return 'rgba(255, 193, 7, 0.2)';
    if (props.$variant === 'primary') return 'rgba(76, 175, 80, 0.2)';
    if (props.$active) return 'rgba(255, 255, 255, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  border: 1px solid ${props => {
    if (props.$variant === 'follow' && props.$active) return 'rgba(76, 175, 80, 0.4)';
    if (props.$variant === 'thumbsUp' && props.$active) return 'rgba(76, 175, 80, 0.4)';
    if (props.$variant === 'thumbsDown' && props.$active) return 'rgba(244, 67, 54, 0.4)';
    if (props.$variant === 'favorite' && props.$active) return 'rgba(255, 193, 7, 0.4)';
    if (props.$variant === 'primary') return 'rgba(76, 175, 80, 0.4)';
    if (props.$active) return 'rgba(255, 255, 255, 0.3)';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  color: ${props => {
    if (props.$variant === 'follow' && props.$active) return '#4CAF50';
    if (props.$variant === 'thumbsUp' && props.$active) return '#4CAF50';
    if (props.$variant === 'thumbsDown' && props.$active) return '#F44336';
    if (props.$variant === 'favorite' && props.$active) return '#FFC107';
    if (props.$variant === 'primary') return '#4CAF50';
    return 'white';
  }};
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      if (props.$variant === 'follow') return props.$active ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.1)';
      if (props.$variant === 'thumbsUp') return props.$active ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.1)';
      if (props.$variant === 'thumbsDown') return props.$active ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.1)';
      if (props.$variant === 'favorite') return props.$active ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.1)';
      if (props.$variant === 'primary') return 'rgba(76, 175, 80, 0.3)';
      return 'rgba(255, 255, 255, 0.2)';
    }};
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 16px;
  }
`;

const VoteCount = styled.span`
  font-size: 12px;
  opacity: 0.8;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
  transition: transform 0.2s ease;

  &:hover {
    color: white;
  }
`;

const ContributionsSection = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '600px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const ContributionsContent = styled.div`
  padding: 24px;
`;

const ContributionCategory = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h4`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    opacity: 0.6;
  }
`;

const ContributionItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContributionName = styled.span`
  color: white;
  font-size: 14px;
`;

const ContributionDetails = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
`;

const ContributorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const ContributorAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.1);
`;

const ContributorLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 13px;
  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

const SourceMeditationLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  &:hover {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: underline;
  }
`;

interface MeditationData {
  script_name: string;
  script_description: string;
  artUrl: string;
  final_audio_file: string;
  duration: number;
  thumbs_up: number;
  thumbs_down: number;
  views: number;
  listens: number;
  completions: number;
  auto_loop: boolean;
  is_anonymous: boolean;
  contributions: any;
  published_at: string;
  username: string;
  user_id: string;
  profile_image?: string;
  tags: string[];
  user_has_voted?: 'up' | 'down' | null;
  user_has_favorited?: boolean;
  user_is_following?: boolean;
}

// Helper function to format relative time
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 172800) return 'yesterday';
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

/**
 * Meditation Page Component - Displays individual meditation for playback
 * 
 * This component fetches meditation data from the backend API using the ID
 * from the URL and displays it using the MeditationDisplay component.
 */
const Meditation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser();
  const [meditation, setMeditation] = useState<MeditationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const fetchingRef = useRef<string | null>(null);

  console.log('Meditation component rendered with ID:', id);

  useEffect(() => {
    const fetchMeditation = async () => {
      if (!id) {
        setError('No meditation ID provided');
        setLoading(false);
        return;
      }

      // Prevent duplicate calls for the same ID
      if (fetchingRef.current === id) {
        console.log('Already fetching meditation for ID:', id);
        return;
      }

      fetchingRef.current = id;
      console.log('Fetching meditation data for ID:', id);
      
      try {
        setLoading(true);
        setError(null);
        const data = await authService.getMeditationForDisplay(id);
        console.log('Meditation data received:', data.script_name);
        setMeditation(data);
      } catch (err: any) {
        console.error('Error fetching meditation:', err);
        
        if (err.response?.status === 404) {
          setError('Meditation not found or not published');
        } else if (err.response?.status === 401) {
          setError('Authentication required to view this meditation');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this meditation');
        } else {
          setError('Failed to load meditation. Please try again.');
        }
      } finally {
        setLoading(false);
        fetchingRef.current = null; // Clear the ref when done
      }
    };

    fetchMeditation();
  }, [id]);

  if (loading) {
    return (
      <Container>
        <Loading>Loading meditation...</Loading>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        {error.includes('not found') ? (
          <NotFound>
            <NotFoundTitle>Meditation Not Found</NotFoundTitle>
            <NotFoundMessage>
              The meditation you're looking for doesn't exist or isn't published yet.
            </NotFoundMessage>
          </NotFound>
        ) : (
          <Error>{error}</Error>
        )}
      </Container>
    );
  }

  if (!meditation) {
    return (
      <Container>
        <Error>No meditation data available</Error>
      </Container>
    );
  }

  // Construct full URLs for art and audio
  const artUrl = meditation.artUrl ? `${meditation.artUrl}` : undefined;
  const audioUrl = meditation.final_audio_file ? `${meditation.final_audio_file}` : undefined;

  // Helper function to show sign-in modal with pending action
  const requireAuth = (action: () => void) => {
    if (!isLoggedIn || !user) {
      setPendingAction(() => action);
      setShowSignInModal(true);
      return false;
    }
    return true;
  };

  // Action handlers
  const handleFollow = async () => {
    if (!meditation) return;
    
    if (!requireAuth(handleFollow)) return;
    
    try {
      const result = await authService.toggleFollowUser(meditation.user_id);
      setMeditation(prev => prev ? {
        ...prev,
        user_is_following: result.is_following
      } : null);
    } catch (error) {
      console.error('Follow error:', error);
    }
  };

  const handleThumbsUp = async () => {
    if (!meditation || !id) return;
    
    if (!requireAuth(handleThumbsUp)) return;
    
    try {
      const result = await authService.rateScript(id, 'like');
      setMeditation(prev => prev ? {
        ...prev,
        user_has_voted: result.user_rating === 'like' ? 'up' : result.user_rating === 'dislike' ? 'down' : null,
        thumbs_up: result.thumbs_up,
        thumbs_down: result.thumbs_down
      } : null);
    } catch (error) {
      console.error('Thumbs up error:', error);
    }
  };

  const handleThumbsDown = async () => {
    if (!meditation || !id) return;
    
    if (!requireAuth(handleThumbsDown)) return;
    
    try {
      const result = await authService.rateScript(id, 'dislike');
      setMeditation(prev => prev ? {
        ...prev,
        user_has_voted: result.user_rating === 'like' ? 'up' : result.user_rating === 'dislike' ? 'down' : null,
        thumbs_up: result.thumbs_up,
        thumbs_down: result.thumbs_down
      } : null);
    } catch (error) {
      console.error('Thumbs down error:', error);
    }
  };

  const handleFavorite = async () => {
    if (!meditation || !id) return;
    
    if (!requireAuth(handleFavorite)) return;
    
    try {
      const result = await authService.toggleFavoriteScript(id);
      setMeditation(prev => prev ? {
        ...prev,
        user_has_favorited: result.is_favorited
      } : null);
    } catch (error) {
      console.error('Favorite error:', error);
    }
  };

  const handleCustomize = async () => {
    if (!id) {
      console.error('No meditation ID available');
      return;
    }

    if (!requireAuth(handleCustomize)) return;

    try {
      // Check if meditation data is available
      if (!meditation) {
        console.error('No meditation data available');
        return;
      }

      // Call the customize API - backend will handle permission validation
      const result = await authService.customizeScript(id);
      
      // Navigate to the appropriate edit page based on is_unguided flag
      const newScriptId = result.script.id;
      if (result.script.is_unguided) {
        navigate(`/edit/${newScriptId}/audio`);
      } else {
        navigate(`/edit/${newScriptId}/write`);
      }
      
    } catch (error) {
      console.error('Customize error:', error);
      
      // Handle specific error messages
      const errorMessage = (error as any)?.response?.data?.error || (error as Error)?.message || 'Unknown error';
      
      if (errorMessage.includes('Authentication required')) {
        navigate('/');
      } else if (errorMessage.includes('Script not found or cannot be customized')) {
        alert('This meditation cannot be customized. It may be private, protected, or not published.');
      } else if (errorMessage.includes('User not found')) {
        alert('User authentication error. Please try logging out and back in.');
      } else {
        alert('Failed to customize meditation. Please try again.');
      }
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleSignInSuccess = () => {
    setShowSignInModal(false);
    // After successful sign in, execute the pending action
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  // Render contributions
  const renderContributions = () => {
    if (!meditation.contributions) return null;

    const { audio_sources, brainwave, noise, script_parts } = meditation.contributions;

    return (
      <>
        {audio_sources && audio_sources.length > 0 && (
          <ContributionCategory>
            <CategoryTitle>
              <Icon icon={FaMusic} />
              Audio Sources
            </CategoryTitle>
            {audio_sources.map((audio: any, index: number) => (
              <ContributionItem key={index}>
                {audio.contributor_username && (
                  <ContributorInfo>
                    <ContributorAvatar 
                      src={audio.contributor_profile_image ? `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${audio.contributor_profile_image}` : `/images/profile/default48.png`}
                      alt={audio.contributor_username}
                    />
                    <ContributorLink to={`/profile/${audio.contributor_username}`}>
                      {audio.contributor_username}
                    </ContributorLink>
                  </ContributorInfo>
                )}
                <ContributionName>{audio.audio_name}</ContributionName>
                <ContributionDetails>{audio.audio_type}</ContributionDetails>
              </ContributionItem>
            ))}
          </ContributionCategory>
        )}

        {brainwave && brainwave.length > 0 && (
          <ContributionCategory>
            <CategoryTitle>
              <Icon icon={FaBrain} />
              Brainwave Entrainment
            </CategoryTitle>
            {brainwave.map((bw: any, index: number) => (
              <ContributionItem key={index}>
                <ContributionName>{bw.modulation_type}</ContributionName>
                <ContributionDetails>
                  {bw.min_brainwave_freq} - {bw.max_brainwave_freq}
                </ContributionDetails>
              </ContributionItem>
            ))}
          </ContributionCategory>
        )}

        {noise && noise.length > 0 && (
          <ContributionCategory>
            <CategoryTitle>
              <Icon icon={FaVolumeUp} />
              Noise Generators
            </CategoryTitle>
            {noise.map((n: any, index: number) => (
              <ContributionItem key={index}>
                <ContributionName>{n.noise_name}</ContributionName>
              </ContributionItem>
            ))}
          </ContributionCategory>
        )}

        {script_parts && script_parts.filter((p: any) => !p.is_original).length > 0 && (
          <ContributionCategory>
            <CategoryTitle>
              <Icon icon={FaFileAudio} />
              Script Parts
            </CategoryTitle>
            {script_parts.filter((p: any) => !p.is_original).map((part: any, index: number) => (
              <ContributionItem key={index}>
                {part.source_username && (
                  <ContributorInfo>
                    <ContributorAvatar 
                      src={part.source_profile_image ? `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${part.source_profile_image}` : `/images/profile/default48.png`}
                      alt={part.source_username}
                    />
                    <ContributorLink to={`/profile/${part.source_username}`}>
                      {part.source_username}
                    </ContributorLink>
                  </ContributorInfo>
                )}
                <ContributionName>{part.source_part_name || part.part}</ContributionName>
                <ContributionDetails>
                  from {part.source_script_id ? (
                    <SourceMeditationLink to={`/meditation/${part.source_script_id}`}>
                      {part.source_script_name || 'Community'}
                    </SourceMeditationLink>
                  ) : (
                    part.source_script_name || 'Community'
                  )}
                </ContributionDetails>
              </ContributionItem>
            ))}
          </ContributionCategory>
        )}
      </>
    );
  };

  return (
    <>
      {meditation ? (
        <SEO 
          title={`${meditation.script_name} - YouMeditation`}
          description={meditation.script_description || `Experience "${meditation.script_name}" - a ${meditation.duration ? `${Math.round(meditation.duration / 60)} minute ` : ''}meditation by ${meditation.username || 'YouMeditation'}. Join thousands discovering mindful moments.`}
          keywords={`meditation, ${meditation.tags?.join(', ') || 'mindfulness, relaxation'}, guided meditation, ${meditation.username || 'YouMeditation'}`}
          image={artUrl}
          url={`/meditation/${id}`}
          author={meditation.username}
          type="article"
        />
      ) : (
        <SEO 
          title="Meditation - YouMeditation"
          description="Discover a unique meditation experience on YouMeditation. Join thousands exploring mindfulness and relaxation."
          keywords="meditation, mindfulness, guided meditation, relaxation, wellness"
          url={`/meditation/${id}`}
          type="article"
        />
      )}
      <Container>
      <FreeListenBanner onSignUpClick={() => setShowSignInModal(true)} />

      <MeditationDisplay
        title={meditation.script_name}
        description={meditation.script_description}
        tags={meditation.tags}
        artUrl={artUrl}
        audioUrl={audioUrl}
        duration={meditation.duration}
        autoLoop={meditation.auto_loop}
        scriptId={id}
      />
      
      <ContentBox>
        <ContentHeader>
          <UserInfo>
            <AvatarLink to={`/profile/${meditation.username}`}>
              <Avatar 
                src={meditation.profile_image ? `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${meditation.profile_image}` : `/images/profile/default48.png`} 
                alt={meditation.username}
              />
            </AvatarLink>
            <UserDetails>
              <UserLink to={`/profile/${meditation.username}`}>
                <Username>{meditation.username}</Username>
              </UserLink>
              <ViewCount>{meditation.views.toLocaleString() || 0} views</ViewCount>
              <PublishedDate>{getRelativeTime(meditation.published_at)}</PublishedDate>
            </UserDetails>
          </UserInfo>
          
          <ActionButtons>
            {meditation.user_id !== "" && (
              <ActionButton 
                $variant="follow" 
                $active={meditation.user_is_following} 
                onClick={handleFollow}
              >
                <Icon icon={FaUserPlus} />
                {meditation.user_is_following ? 'Following' : 'Follow'}
              </ActionButton>
            )}
            
            <ActionButton 
              $variant="thumbsUp" 
              $active={meditation.user_has_voted === 'up'} 
              onClick={handleThumbsUp}
            >
              <Icon icon={FaThumbsUp} />
              <VoteCount>{meditation.thumbs_up || 0}</VoteCount>
            </ActionButton>
            
            <ActionButton 
              $variant="thumbsDown" 
              $active={meditation.user_has_voted === 'down'} 
              onClick={handleThumbsDown}
            >
              <Icon icon={FaThumbsDown} />
              <VoteCount>{meditation.thumbs_down || 0}</VoteCount>
            </ActionButton>
            
            <ActionButton 
              $variant="favorite" 
              $active={meditation.user_has_favorited} 
              onClick={handleFavorite}
            >
              <Icon icon={FaStar} />
            </ActionButton>
            
            <ActionButton onClick={handleCustomize}>
              Customize
            </ActionButton>
            
            <ActionButton onClick={handleShare}>
              <Icon icon={FaShare} />
            </ActionButton>
            
            <ExpandButton onClick={() => setIsExpanded(!isExpanded)}>
              <Icon icon={isExpanded ? FaChevronUp : FaChevronDown} size={20} />
            </ExpandButton>
          </ActionButtons>
        </ContentHeader>
        
        <ContributionsSection $isExpanded={isExpanded}>
          <ContributionsContent>
            {renderContributions()}
          </ContributionsContent>
        </ContributionsSection>
      </ContentBox>

      {/* Comments Section */}
      <Comments 
        targetType="script" 
        targetId={id!} 
        targetOwnerId={meditation.user_id}
      />
    </Container>
    
    {/* Share Modal */}
    {meditation && (
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        url={`/meditation/${id}`}
        title={`${meditation.script_name} - YouMeditation`}
        description={meditation.script_description || 'Check out this meditation on YouMeditation'}
      />
    )}
    
    <SignInModal
      isOpen={showSignInModal}
      onClose={() => setShowSignInModal(false)}
      onSuccess={handleSignInSuccess}
    />
    </>
  );
};

export default Meditation; 